import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Mail, MessageSquare, MonitorSmartphone, CheckCircle2, Clock, XCircle, Download } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { usePaged, Paginator } from "../components/Paginator";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const CHANNEL_ICON = { sms: MessageSquare, email: Mail, portal: MonitorSmartphone };
const STATUS_STYLE = {
  sent: { bg: "#17A88A1A", color: "#17A88A", label: "Sent", icon: CheckCircle2 },
  simulated: { bg: "#0844381A", color: "#084438", label: "Simulated", icon: Clock },
  failed: { bg: "#C0392B1A", color: "#C0392B", label: "Failed", icon: XCircle },
};
const CATEGORY_LABEL = {
  welcome: "Welcome", credentials: "Login details", "check-in": "Check-in",
  announcement: "Announcement", reminder: "Reminder",
};

function Stat({ label, value, tint, testid }) {
  return (
    <div data-testid={testid} className={`${CARD} p-5`}>
      <div className="font-head text-3xl font-extrabold tracking-tight" style={{ color: tint }}>{value}</div>
      <div className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>{label}</div>
    </div>
  );
}

export default function MessageLog() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, sent: 0, simulated: 0, failed: 0 });
  const [trend, setTrend] = useState([]);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages/log", { params: {
        channel: channel || undefined, status: status || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
      } });
      setItems(data.items);
      setSummary(data.summary);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [channel, status, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/messages/log/trend").then(({ data }) => setTrend(data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/messages/log/export", { responseType: "blob", params: {
        channel: channel || undefined, status: status || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
      } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `message_log_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Message log exported");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('en-GB', { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const Filter = ({ value, cur, set, children, testid }) => (
    <button data-testid={testid} onClick={() => set(cur === value ? "" : value)}
      className="rounded-full px-4 py-2 text-sm font-semibold border transition-all"
      style={cur === value ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
      {children}
    </button>
  );

  const { page, setPage, pageCount, pageItems } = usePaged(items, 25);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="message-log-page">
      <div className="sj-page-hero flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--sj-text-3)" }}>
            <Send className="h-4 w-4" /> Messaging
          </div>
          <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Messages sent</h1>
          <p style={{ color: "var(--sj-text-2)" }}>An audit trail of every automated & manual message the system has sent.</p>
        </div>
        <button data-testid="export-log-button" onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all hover:bg-white" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="log-stat-total" label="Total messages" value={summary.total} tint="#0B5E4E" />
        <Stat testid="log-stat-sent" label="Sent for real" value={summary.sent} tint="#17A88A" />
        <Stat testid="log-stat-simulated" label="Simulated" value={summary.simulated} tint="#084438" />
        <Stat testid="log-stat-failed" label="Failed" value={summary.failed} tint="#C0392B" />
      </div>

      {summary.simulated > 0 && summary.sent === 0 && (
        <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "#0844381A", color: "#084438" }}>
          Messages are currently <strong>simulated</strong>. Add your Twilio (SMS) and Resend (Email) keys to start sending for real.
        </div>
      )}

      {trend.some((t) => t.sms + t.email > 0) && (
        <div className={`${CARD} p-6`} data-testid="message-trend-chart">
          <h3 className="font-head text-lg font-bold mb-4" style={{ color: "var(--sj-text)" }}>Messages sent · last 6 months</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trend} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E4" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#56626A", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#56626A", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBE9E4" }} />
              <Legend />
              <Bar dataKey="sms" name="SMS" fill="#0B5E4E" radius={[6, 6, 0, 0]} />
              <Bar dataKey="email" name="Email" fill="#17A88A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Filter value="sms" cur={channel} set={setChannel} testid="filter-channel-sms">SMS</Filter>
        <Filter value="email" cur={channel} set={setChannel} testid="filter-channel-email">Email</Filter>
        <span className="w-px h-6 bg-[#EBE9E4] mx-1" />
        <Filter value="sent" cur={status} set={setStatus} testid="filter-status-sent">Sent</Filter>
        <Filter value="simulated" cur={status} set={setStatus} testid="filter-status-simulated">Simulated</Filter>
        <Filter value="failed" cur={status} set={setStatus} testid="filter-status-failed">Failed</Filter>
        <span className="w-px h-6 bg-[#EBE9E4] mx-1" />
        <div className="flex items-center gap-1.5 text-sm" style={{ color: "var(--sj-text-2)" }}>
          <input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#17A88A]" />
          <span style={{ color: "var(--sj-text-3)" }}>to</span>
          <input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#17A88A]" />
        </div>
        {(dateFrom || dateTo || channel || status) && (
          <button data-testid="clear-filters" onClick={() => { setChannel(""); setStatus(""); setDateFrom(""); setDateTo(""); }}
            className="text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Clear</button>
        )}
      </div>

      {loading ? (
        <div className={`${CARD} p-12 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
          <Send className="h-12 w-12" />
          No messages logged yet. Messages appear here as the system sends welcomes, logins, check-ins and announcements.
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">When</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">Recipient</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">Type</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">Channel</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">Status</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-xs">By</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((m) => {
                  const st = STATUS_STYLE[m.status] || STATUS_STYLE.sent;
                  const CIcon = CHANNEL_ICON[m.channel] || Send;
                  const SIcon = st.icon;
                  return (
                    <tr key={m.id} data-testid={`log-row-${m.id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                      <td className="py-3.5 px-5 whitespace-nowrap" style={{ color: "var(--sj-text-2)" }}>{fmt(m.created_at)}</td>
                      <td className="py-3.5 px-5">
                        <div className="font-semibold" style={{ color: "var(--sj-text)" }}>{m.member_name || "—"}</div>
                        <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{m.target}</div>
                      </td>
                      <td className="py-3.5 px-5" style={{ color: "var(--sj-text-2)" }}>{CATEGORY_LABEL[m.category] || m.category}</td>
                      <td className="py-3.5 px-5">
                        <span className="inline-flex items-center gap-1.5 capitalize" style={{ color: "var(--sj-text-2)" }}><CIcon className="h-4 w-4" /> {m.channel}</span>
                      </td>
                      <td className="py-3.5 px-5">
                        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: st.bg, color: st.color }}>
                          <SIcon className="h-3.5 w-3.5" /> {st.label}
                        </span>
                      </td>
                      <td className="py-3.5 px-5" style={{ color: "var(--sj-text-3)" }}>{m.author}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-3"><Paginator page={page} pageCount={pageCount} onPage={setPage} testid="message-log-paginator" /></div>
        </div>
      )}
    </div>
  );
}
